import GenericTabs from 'components/navigation/Tabs/Tabs';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useMemo, useState } from 'react';
import { RiskRealEstate } from './RiskRealEstate';
import { RiskParc } from './RiskParc';
import { ParcTenantsByRealEstate } from './ParcTenantsByRealEstate';
import { TenantsDocumentsManager } from './TenantsDocumentsManager';
import { useTabErrors } from 'components/navigation/Tabs/useTabErrors';
import { GenericLoading } from 'components/ui/Loader';
import { useQuoteContractualizationData } from '../../store';

export const RgiRisk: React.FC<{ refresh: () => void }> = ({ refresh }) => {
  const quoteData = useQuoteContractualizationData();
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const [active, setActive] = useState<number | string>(1);

  // Mode parc : les locataires sont portés par chaque bien couvert (tenants reste vide)
  const isParc = !!quoteData?.parc;

  const allTenants = useMemo(() => {
    if (isParc) {
      return (quoteData?.parcRealEstates ?? []).flatMap(bien => bien.tenants);
    }
    return quoteData?.tenants ?? [];
  }, [isParc, quoteData]);

  const errors = useMemo(() => {
    return allTenants.reduce((acc, current) => {
      if (current.isEligible && !current.isCompleted) {
        return {
          ...acc,
          [current.id]: 'Pas eligible'
        };
      }
      return acc;
    }, {});
  }, [allTenants]);

  const tabFieldsConfig = useMemo(() => {
    return {
      2: allTenants.map(tenant => tenant.id)
    };
  }, [allTenants]);

  const { getTabNotification } = useTabErrors(errors, tabFieldsConfig);

  if (!quoteData || (!quoteData.tenants && !isParc)) {
    return <GenericLoading loading isParentSized />;
  }

  return (
    <GenericTabs defaultTab={active} onChange={setActive}>
      <GenericTabs.Item
        label={t(isParc ? 'REAL_ESTATE.SECTIONS.PARC_INFOS' : 'REAL_ESTATE.SECTIONS.REAL_ESTATE_INFOS')}
        value={1}
      />
      <GenericTabs.Content value={1}>
        {isParc ? <RiskParc /> : <RiskRealEstate />}
      </GenericTabs.Content>
      <GenericTabs.Item
        label={t('REAL_ESTATE.LABELS.TENANTS')}
        value={2}
        notification={getTabNotification(2)}
      />
      <GenericTabs.Content value={2}>
        {isParc ? (
          <ParcTenantsByRealEstate
            refresh={refresh}
            realEstates={quoteData.parcRealEstates ?? []}
          />
        ) : (
          <TenantsDocumentsManager refresh={refresh} tenants={quoteData.tenants ?? []} />
        )}
      </GenericTabs.Content>
    </GenericTabs>
  );
};
